import { Business } from '../business/business';
import { PhoneNumber } from '../phone/phone';

export type CallRateLimitCallerType = 'test_number' | 'transfer_destination';

/** A business-owned number the API is denying for too many test calls (ENG-2263). */
export interface CallRateLimit {
  number: string;
  callerType: CallRateLimitCallerType;
  window: 'short' | 'long';
  callsMade: number;
  maxCalls: number;
  retryAfterSeconds: number;
}

export interface VoiceOption {
  value: string;
  label: string;
  gender: 'women' | 'men';
  flag: 'US' | 'GB' | 'ES' | string;
}

export interface AgentConfigOptions {
  toneOptions: string[];
  voiceOptions: VoiceOption[];
  backgroundNoiseOptions: { label: string, value: string }[];
  voiceSpeedOptions?: { value: string, label: string, shortLabel?: string }[];
  languages: { value: string, label: string }[];
  nameFormatOptions?: { label: string, value: string }[];
  holdMusicOptions: { value: string, label: string, url: string }[];
  voicePreviewText?: string;
}

export interface AgentData {
  title: string;
  configuration: {
    voice: string,
    tone?: string;
  };
}

export type CalendarAvailabilityType = 'business_hours' | 'custom';

export type CalendarDateOverrideType = 'hours' | 'all_day' | 'unavailable';

export interface CalendarCustomHour {
  day: string;
  open: string;
  close: string;
}

export interface CalendarDateOverride {
  startDate: string;
  endDate?: string | null;
  type: CalendarDateOverrideType;
  open?: string;
  close?: string;
}

export interface CalendarConfig {
  meetingDuration: number;
  meetingTitle: string;
  meetingEventType?: string | null;
  calendarId?: string | null;
  attendeeEmails?: string[];
  // Absent means the merchant has never touched the setting, which reads as on.
  collectCallerEmail?: boolean;
  // null means the merchant has never configured conflict calendars, so the
  // booking calendar is seeded. An empty array is a deliberate choice.
  conflictCalendarIds?: string[] | null;
  availabilityType?: CalendarAvailabilityType;
  customHours?: CalendarCustomHour[] | null;
  maxDaysInAdvance?: number;
  minLeadTimeHours?: number;
  dateOverrides?: CalendarDateOverride[];
}

export interface Agent {
  canBlockCallsBeforeConnect?: boolean;
  calendar?: {
    details?: {
      reference: 'google_calendar' | 'calendly' | string;
      timezone: string;
      account: string;
      name: string;
      eventTypes?: Array<{
        uri: string;
        name: string;
        active: boolean;
        duration: number;
        schedulingUrl: string;
      }>;
    }
    config?: CalendarConfig;
    calendars?: Array<{ id: string; name: string }>;
  };
  id?: string;
  businessId?: number;
  title?: string;
  configuration?: Configuration;
  phoneNumbers?: PhoneNumber[];
  business?: Business;
  externalId?: string;
  isAgentGenerated?: boolean;
  trialSecondsRemaining?: number;
  bookingMessage?: Booking;
  blockedPhoneNumbers?: PhoneNumber[];
  callRateLimits?: CallRateLimit[];
  sms: boolean;
  trainingStatus?: TrainingStatus;
  hasExpiredPhoneNumber?: boolean;
  providerId?: number;
  greeting?: {
    default?: string;
    legalDisclaimer?: string;
  }
  scenarios?: MessageScenario[];
}

export interface Configuration {
  initialSetupCompleted?: boolean;
  talkToRosieCompleted?: boolean;
  tone?: string;
  voiceId?: string;
  backgroundNoise?: BackgroundNoiseType;
  voiceSpeed?: string;
  hasAppointmentsEnabled?: boolean;
  hasAppointmentSchedulingEnabled?: boolean;
  hasTextLinkEnabled?: boolean;
  hasTransferCallsEnabled?: boolean;
  hasCustomQuestionsForNotesEnabled?: boolean;
  hasCustomSummaryEnabled?: boolean;
  customSummary?: string;
  hasCustomGreetingEnabled?: boolean;
  customGreeting?: string;
  hasLegalDisclaimerEnabled?: boolean;
  legalDisclaimer?: string;
  appointmentLengthMinutes?: number;
  calendarConfig?: CalendarConfig;
  hasBlock_1800NumbersEnabled?: boolean;
  hasSmartSpamDetectionEnabled?: boolean;
  hasBlockCallsBeforeConnectEnabled?: boolean;
  hasMessageScenariosEnabled?: boolean;
  suggestedScenarios?: { title: string; text: string }[];
  testCallQuestions?: TestCallQuestions;
  callFinishedNotificationChannel?: CallFinishedNotificationChannel[];
  languages?: string[];
  numberForwardingEnabled?: boolean;
  lastActiveConfigurationStep?: string;
  trainStepCompleted?: boolean;
  customizeStepCompleted?: boolean;
  launchStepViewed?: boolean;
  selectedPhoneSystemType?: string;
  selectedPhoneProvider?: string;
  useRosieNumberSelected?: boolean;
  liveBannerDismissed?: boolean;
  testStepCompleted?: boolean;
  smsWidget?: {
    primaryColor?: string;
    position?: 'bottom-right' | 'bottom-left';
    avatar?: string;
    greetingMessage?: string;
    chatGreetingMessage?: string;
    widgetOpenMode?: 'immediately' | 'after_delay' | 'on_click';
    widgetOpenDelay?: number;
  };
  smsWidgetAllowedDomains?: (string | SmsWidgetAllowedDomain)[];
  agentKnowledge?: AgentKnowledge;
  websiteTextingPreSelected?: boolean;
  hasAlternativeGreetingEnabled?: boolean;
  alternativeGreeting?: string;
  alternativeGreetingHoursType?: AlternativeGreetingHoursType;
  alternativeGreetingCustomHours?: AlternativeGreetingCustomHour[];
  hasAlternativeGreetingLegalDisclaimerEnabled?: boolean;
  askForName?: boolean | null;
  nameFormat?: string;
  requestCallerSpellName?: boolean;
}

// Business-specific "Try asking..." questions written after onboarding
// training (ENG-2333). Shown after the static "Tell me about <business>?".
export enum TestCallQuestionsStatus {
  Pending = 'pending',
  Ready = 'ready',
  Static = 'static'
}

export interface TestCallQuestions {
  status: TestCallQuestionsStatus;
  questions?: string[];
}

export interface TrainingFaq {
  question: string;
  answer: string;
  id?: string;
}

export interface CustomQuestion {
  question: string;
  id?: string;
}

export interface MessageScenario {
  id?: string;
  title: string;
  text: string;
  priority?: number;
  suggested?: boolean;
}

export enum CustomQuestionType {
  CalendarEvent = 'calendar_event',
  Note = 'note'
}

export enum CalendarProvider {
  GoogleCalendar = 'googlecalendar',
  Calendly = 'calendly',
  Appointlet = 'appointlet',
  Acuity = 'acuity'
}

export interface TrainingFile {
  originalFileName: string;
  id: string;
  name?: string;
  description?: string;
}

export interface Booking {
  message: string;
  url: string;
  id?: string;
}

export enum BackgroundNoiseType {
  None = 'none',
  Cafe = 'cafe',
  Office = 'office',
}

export type AlternativeGreetingHoursType = 'after_hours' | 'custom';

export interface AlternativeGreetingCustomHour {
  day: string;
  open: string;
  close: string;
  allDay?: boolean;
}

export enum CallFinishedNotificationChannel {
  Email = 'email',
  SMS = 'sms',
  Push = 'push'
}

export interface AgentKnowledge {
  content: string;           // Max 3000 chars, HTML formatted
  isEdited: boolean;
  lastUpdated?: string;
  sourceType: 'scraped' | 'manual';
}

export interface ScrapeStatus {
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  pagesScraped?: number;
  totalPages?: number;
  error?: string;
}

export enum TrainingStatus {
  InProgress = 'in_progress',
  Failed = 'failed',
  Finished = 'finished'
}

export interface SmsWidgetAllowedDomain {
  domain: string;
  autoAdded: boolean;
}

export interface CrawledUrl {
  url: string;
  title?: string;
  active: boolean;
  id?: string;
}
